import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { TemplateState } from '../../../types/common/types';
import { GzLabeledContentWebComponent } from '../../../ui/gzLabeledContent';
import {
  getMdrServiceDateString,
  getMdrServiceFlavorString,
  getMdrWarrantyDateString,
  getMdrWarrantyStatusWithBullet,
  getStatusWithBullet,
} from '../../../company/common/licensingTab/MdrUtils';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { translateFn } from '../../../utils/translator.helper';
import { CompanyData, LicenseAddon, ProductService } from '../../../company/types/company-data';
import { MdrServiceFlavor } from '../../../license/types/MdrServiceFlavor';
import { BITDEFENDER_MDR_LOGO, BITDEFENDER_MDR_PLUS_LOGO } from '../types/MdrLogos';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';
import { ProductServiceStatus } from '../../../license/types/ProductServiceStatus';
import { MAX_DAYS_FOR_INACTIVE_STATUS } from '../../common/constants';
import { augmentTextWithValue, calculateRemainingDays, getPluralisedTextByDay } from '../../../utils/global.utils';
import { BREACH_WARRANTY_PRODUCTS, PRODUCT_ID_TO_NAME } from '@nirvana/shared/license-helper';

export class MdrCustomerPanelView extends BaseWebComponentView {
  dom: {
    $wrapper: HTMLElement;
    $mdrIcon: HTMLElement;
    $mdrText: GzTextWebComponent;

    $ssoInfoBoxWrapper: HTMLElement;
    $ssoInfoBox: GzInfoBoxWebComponent;

    $licenseExpirationInfoBox: GzInfoBoxWebComponent;

    mdrDetails: {
      licenseInfo: {
        $mdrSubscriptionType: GzLabeledContentWebComponent;
        $mdrStartEndDate: GzLabeledContentWebComponent;
        $mdrStatus: GzLabeledContentWebComponent;
      };
      warrantyInfo: {
        $mdrWarrantyType: GzLabeledContentWebComponent;
        $mdrWarrantyDate: GzLabeledContentWebComponent;
        $mdrWarrantyStatus: GzLabeledContentWebComponent;
      };
    };

    $warrantyInfo: HTMLElement;
    $openConsoleButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $mdrIcon: this.shadow.querySelector('#mdr-logo'),
      $mdrText: this.shadow.querySelector('#mdr-text'),
      $ssoInfoBoxWrapper: this.shadow.querySelector('#sso-infobox-wrapper'),
      $ssoInfoBox: this.shadow.querySelector('#sso-disabled'),
      $licenseExpirationInfoBox: this.shadow.querySelector('#license-expiration-info'),

      mdrDetails: {
        licenseInfo: {
          $mdrSubscriptionType: this.shadow.querySelector('#mdr-subscription-type'),
          $mdrStartEndDate: this.shadow.querySelector('#mdr-start-end-date'),
          $mdrStatus: this.shadow.querySelector('#mdr-status'),
        },
        warrantyInfo: {
          $mdrWarrantyType: this.shadow.querySelector('#mdr-warranty-type'),
          $mdrWarrantyDate: this.shadow.querySelector('#mdr-warranty-end-date'),
          $mdrWarrantyStatus: this.shadow.querySelector('#mdr-warranty-status'),
        },
      },

      $warrantyInfo: this.shadow.querySelector('#mdr-warranty-info'),
      $openConsoleButton: this.shadow.querySelector('#open-console-button'),
    };

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getMdrServiceStrings();
  }

  public setTranslatedStrings() {
    this.dom.$mdrText.innerText = this.translations.mdrText;

    this.dom.mdrDetails.licenseInfo.$mdrSubscriptionType.label = translateFn('Service model');
    this.dom.mdrDetails.licenseInfo.$mdrStartEndDate.label = translateFn('{MDR_SERVICE} entitlement period', {
      MDR_SERVICE: 'MDR',
    });
    this.dom.mdrDetails.licenseInfo.$mdrStatus.label = translateFn('Status');

    this.dom.mdrDetails.warrantyInfo.$mdrWarrantyType.label = translateFn('Warranty add-on');
    this.dom.mdrDetails.warrantyInfo.$mdrWarrantyDate.label = translateFn('Warranty validity');
    this.dom.mdrDetails.warrantyInfo.$mdrWarrantyStatus.label = translateFn('Warranty status');

    this.dom.$openConsoleButton.innerText = this.translations.openConsole;

    this.dom.$ssoInfoBox.innerText = this.translations.disabledSsoInfoBox;
  }

  setLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  removeLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(data: CompanyData) {
    this.buildMdrSection(data);
    this.removeLoadingState();
  }

  public setSso(enabled: boolean) {
    if (enabled) {
      this.enableSso();
    } else {
      this.disableSso();
    }
  }

  private enableSso() {
    this.dom.$openConsoleButton.disabled = false;
    this.dom.$ssoInfoBoxWrapper.classList.add('hidden');
  }

  private disableSso() {
    this.dom.$openConsoleButton.disabled = true;
    this.dom.$ssoInfoBoxWrapper.classList.remove('hidden');
  }

  private buildMdrSection(data: CompanyData) {
    const mdrProductService = data.specifics.productServices?.find(service => service.type === 1);
    const breachWarrantyAddon = data?.specifics?.licenseAddons?.find(addon =>
      BREACH_WARRANTY_PRODUCTS.includes(addon.productId)
    );

    if (!mdrProductService) {
      this.hideMdrSection();
      return;
    }

    toggleVisibility(this.dom.$licenseExpirationInfoBox, false);

    this.showMdrLogo(mdrProductService.flavour);
    this.buildMdrLicenseSection(mdrProductService);

    if (breachWarrantyAddon) {
      this.buildMdrWarrantySection(breachWarrantyAddon);
    } else {
      toggleVisibility(this.dom.$warrantyInfo, false);
    }

    // Show number of days left
    if (mdrProductService.status === ProductServiceStatus.INACTIVE) {
      const displayedDiffDays = calculateRemainingDays(mdrProductService.statusStartDate, MAX_DAYS_FOR_INACTIVE_STATUS);

      const translatedUnitsText = getPluralisedTextByDay(displayedDiffDays);

      this.dom.$licenseExpirationInfoBox.innerHTML = augmentTextWithValue(
        displayedDiffDays,
        1,
        translateFn('Your MDR service account will be removed in'),
        translatedUnitsText,
        translateFn('. Check your licensing status.')
      );

      toggleVisibility(this.dom.$licenseExpirationInfoBox, true);
    }
  }

  private hideMdrSection() {
    toggleVisibility(this.shadow.querySelector('#mdr-container'), false);
  }

  private showMdrLogo(flavor: MdrServiceFlavor): void {
    this.dom.$mdrIcon.innerHTML = `
      <gz-icon class="mdr-logo" color="var(--main-titles-inputs-600)" size="84">
        ${flavor === MdrServiceFlavor.BITDEFENDER_MDR_PLUS ? BITDEFENDER_MDR_PLUS_LOGO : BITDEFENDER_MDR_LOGO}
      </gz-icon>
    `;
  }

  private buildMdrLicenseSection(mdrProductService: ProductService) {
    const mdrFlavourText = getMdrServiceFlavorString(mdrProductService.flavour);
    const mdrLicenseDateText = getMdrServiceDateString(mdrProductService);
    const { bullet, mdrStatusText } = getStatusWithBullet(mdrProductService?.status);

    this.dom.mdrDetails.licenseInfo.$mdrSubscriptionType.innerHTML = `<gz-text fontSize='14' class="content-text">${htmlEncode(
      mdrFlavourText
    )}</gz-text>`;
    this.dom.mdrDetails.licenseInfo.$mdrStartEndDate.innerHTML = `<gz-text fontSize='14' class="content-text">${htmlEncode(
      mdrLicenseDateText
    )}</gz-text>`;
    this.dom.mdrDetails.licenseInfo.$mdrStatus.innerHTML = `<gz-text class='has-bullet ${bullet} content-text' fontSize='14'>${htmlEncode(
      mdrStatusText
    )}</gz-text>`;
  }

  private buildMdrWarrantySection(breachWarrantyAddon: LicenseAddon) {
    toggleVisibility(this.dom.$warrantyInfo, true);

    const warrantyTypeText = PRODUCT_ID_TO_NAME[breachWarrantyAddon.productId]();
    const warrantyDateText = getMdrWarrantyDateString(breachWarrantyAddon);
    const { warrantyBullet, warrantyStatusText } = getMdrWarrantyStatusWithBullet(breachWarrantyAddon.status);

    this.dom.mdrDetails.warrantyInfo.$mdrWarrantyType.innerHTML = `<gz-text fontSize='14' class="content-text">${htmlEncode(
      warrantyTypeText
    )}</gz-text>`;
    this.dom.mdrDetails.warrantyInfo.$mdrWarrantyDate.innerHTML = `<gz-text fontSize='14' class="content-text">${htmlEncode(
      warrantyDateText
    )}</gz-text>`;
    this.dom.mdrDetails.warrantyInfo.$mdrWarrantyStatus.innerHTML = `<gz-text class='has-bullet ${warrantyBullet} content-text' fontSize='14'>${htmlEncode(
      warrantyStatusText
    )}</gz-text>`;
  }
}
